/**
 * How far along a quest a condition is asking about, said as one word.
 *
 * Kept out of the panel that uses it so the panel stays a file of components,
 * and because the one thing worth testing here is the translation rather than
 * the drop-down it fills.
 */

import type { QuestWire } from '@inverse/protocol';
import type { Condition } from './Dialogue';

/**
 * How far along a quest a condition is asking about, as one word.
 *
 * The two numbers underneath say the same thing and say it worse: a step is a
 * number, the end is one past the last step, and nought means not begun. All of
 * that is true and none of it is worth knowing to write a conversation.
 */
export function stageOf(condition: Condition, quests: readonly QuestWire[]): string {
  const steps = quests.find((quest) => quest.id === condition.quest)?.steps.length ?? 0;
  const least = condition.atLeast;
  const most = condition.atMost;

  if (condition.finished === true) return 'finished';
  if (least !== undefined && most !== undefined && least === most) return `step:${least}`;
  if (most === 0) return 'unstarted';
  // Written by an older build, which said finished by naming a step number
  // higher than any quest has.
  if (least !== undefined && steps > 0 && least > steps) return 'finished';
  if (least !== undefined && least >= 1) return 'started';

  return 'any';
}

/** And back again, as the numbers the world actually asks. */
export function stageAt(stage: string): Condition {
  if (stage === 'unstarted') return { atLeast: undefined, atMost: 0, finished: undefined };
  if (stage === 'started') return { atLeast: 1, atMost: undefined, finished: undefined };

  /*
   * Asked of the quest rather than written as a number.
   *
   * This used to say "step 999 or later", which is never true of anything: a
   * quest is finished at one past its last step, so a five step quest finishes
   * at six and a condition waiting for 999 waits for ever. Every "Finished"
   * written in the tools was a condition that could not hold, and nothing said
   * so, because a condition that never holds looks exactly like one whose
   * moment has not come.
   *
   * The world has always been able to answer this properly: past the last step,
   * whatever the last step turns out to be, so adding a step to a quest does
   * not quietly unfinish it for everybody who had finished it.
   */
  if (stage === 'finished') return { atLeast: undefined, atMost: undefined, finished: true };

  const step = /^step:(\d+)$/.exec(stage)?.[1];
  if (step) return { atLeast: Number(step), atMost: Number(step), finished: undefined };

  return { atLeast: undefined, atMost: undefined, finished: undefined };
}
